import { isBugBountyParticipant } from '@atlassian/amkt-common';
import {
  ButtonClickedEvent,
  ErrorStateEvent,
  ScreenViewedEvent,
  SortClickedEvent,
  TileClickedEvent,
  TilesRenderedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import { getAnalyticsFilters } from '@atlassian/mpac-search';
import { AtlassianProductHostingType, CloudFortifiedStatusValues } from '@atlassian/mpac-types';
import { useCallback, useEffect, useRef } from 'react';

import { Analytics } from '../constants/analytics';

import type { ApolloError } from '@apollo/client';
import type { SelectedOptions } from '@atlassian/mpac-primitives';
import type { GetMarketplaceCategoryQuery } from '@atlassian/mpac-services-client';

export const isAppCloudSecurityParticipant = (app) =>
  isBugBountyParticipant(AtlassianProductHostingType.Cloud, app?.programs?.bugBountyParticipant) &&
  app.deployments.includes('cloud');
export const isAppCloudFortified = (app) =>
  app.programs?.cloudFortified?.status === CloudFortifiedStatusValues.Approved;

// eslint-disable-next-line max-lines-per-function
export const useCategoryPageAnalytics = ({
  slug,
  initialFetchError,
  category,
  fetchMoreError,
  appsCount,
  sortKey,
  selectedOptions,
  isLoading,
  isLoadingMore,
  pageRouteSource,
}: {
  slug?: string | null;
  initialFetchError?: ApolloError;
  category: NonNullable<GetMarketplaceCategoryQuery['marketplaceCategory']>['category'] | null;
  fetchMoreError?: ApolloError | null;
  appsCount: number;
  sortKey: string;
  selectedOptions: SelectedOptions | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  pageRouteSource?: string | null;
}) => {
  const fireEvent = useAnalyticsEvents();
  const screenViewedRef = useRef(false);

  const filters = selectedOptions ? getAnalyticsFilters(selectedOptions) : {};

  const fireScreenViewEvent = useCallback(() => {
    if (category && !screenViewedRef.current) {
      screenViewedRef.current = true;
      const attributes = {
        categoryType: category.slug,
        ...(pageRouteSource && { pageRouteSource }),
      };

      fireEvent({
        eventType: ScreenViewedEvent,
        actionSubjectId: Analytics.subjectId.screenRendered,
        attributes,
      });
    }
  }, [category, fireEvent]);

  useEffect(() => {
    if (slug) {
      screenViewedRef.current = false;
    }
  }, [slug]);

  useEffect(() => {
    if (isLoading) return;
    fireScreenViewEvent();
  }, [fireScreenViewEvent, isLoading]);

  const fireAppFetchedEvent = useCallback(() => {
    if (appsCount >= 0 && !initialFetchError && !fetchMoreError) {
      const attributes = {
        categoryType: slug,
        filters: {
          sort: sortKey,
          ...filters,
        },
        count: appsCount,
      };

      fireEvent({
        eventType: TilesRenderedEvent,
        actionSubjectId: Analytics.subjectId.appTilesRendered,
        attributes,
      });
    }
  }, [appsCount, fetchMoreError, initialFetchError, slug, sortKey]);

  const fireAppErrorEvent = useCallback(() => {
    if (!slug || initialFetchError || fetchMoreError) {
      const errorType = !slug
        ? Analytics.event.globalError
        : initialFetchError
        ? Analytics.event.initialError
        : Analytics.event.loadMoreError;
      const actionSubjectId = !slug
        ? Analytics.subjectId.screenRendered
        : Analytics.subjectId.appTilesRendered;

      const attributes = {
        categoryType: slug,
        errorType,
      };

      fireEvent({
        eventType: ErrorStateEvent,
        actionSubjectId,
        attributes,
      });
    }
  }, [fetchMoreError, initialFetchError, slug]);

  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    fireAppFetchedEvent();
  }, [fireAppFetchedEvent, isLoading, isLoadingMore]);

  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    fireAppErrorEvent();
  }, [fireAppErrorEvent, isLoading, isLoadingMore]);

  const fireOnTileClickEvent = (attributes, app) => {
    const allAttributes = {
      ...attributes,
      label: app.addonKey,
      appName: app.name,
      currentPerspective: 'category',
      hitSize: 'large',
      isPaidApp: app?.version?.paid,
      productKeys: app?.version?.compatibilities.map((appData) => appData.applicationName),
      hasCloudSecurityBadge: isAppCloudSecurityParticipant(app),
      hasCloudFortifiedBadge: isAppCloudFortified(app),
    };
    fireEvent({
      eventType: TileClickedEvent,
      actionSubjectId: Analytics.subjectId.appTileClicked,
      attributes: allAttributes,
    });
  };

  const fireOnLoadMoreClickEvent = (attributes) => {
    fireEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: Analytics.subjectId.loadMoreClicked,
      attributes,
    });
  };

  const fireOnClearFiltersClickEvent = () => {
    fireEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: Analytics.subjectId.clearAllClicked,
      attributes: {
        categoryType: category?.slug,
        filters: {
          sort: sortKey,
          ...filters,
        },
      },
    });
  };

  const fireOnSortOptionSelectEvent = (attributes) => {
    fireEvent({
      eventType: SortClickedEvent,
      actionSubjectId: Analytics.subjectId.sortOptionSelected,
      attributes,
    });
  };

  return {
    fireOnTileClickEvent,
    fireOnLoadMoreClickEvent,
    fireOnClearFiltersClickEvent,
    fireOnSortOptionSelectEvent,
  };
};
